import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, FormControl, FormControlLabel, InputLabel,
  MenuItem, Paper, Select, Stack, Step, StepLabel, Stepper, Switch, Typography,
} from '@mui/material';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import { AWS_REGIONS } from '../../../../constants/awsRegions';
import {
  activateIpamConfig, beginIpamConfig, getIpamConfig, getIpamCostPreview,
  getIpamHostPreflight, getIpamQuota, listIpamMembers, recheckIpamQuota,
  requestIpamQuotaIncrease,
} from '../../../../services/ipamApi';
import { listSecurityHubAccounts } from '../../../../services/securityhubApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import MultiSelectField from '../MultiSelectField';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  CK, FONT_BODY, FONT_MONO, actionRowSx, insetPanelSx, sectionCardSx, sectionHeadingSx,
  statusChipSx,
} from '../../portalStyles';
import {
  IPAM_ADVANCED_PRICE, IPAM_ADVANCED_PRICE_MONTHLY, IPAM_ADVANCED_REQUIRED_NOTE,
  IPAM_FREE_TIER_NOTE, IPAM_PRICE_NOTE, formatMonthlyCostUsd,
} from './ipamPricing';
// IMPORTED, NEVER RETYPED. This wizard already stated the adopted truth on the
// Scope & cost step ("Turning VPC IPAM off removes Prism's own associations and
// leaves this IPAM, and its meter, running") forty lines above an alert that
// stated the opposite. Both sentences now come from one module.
import { IPAM_ADOPTED_TEARDOWN_SUMMARY } from './ipamCopy';

// Must match IpamEntry.js and every other IPAM page - see the colour comment
// there for why slate, and why the hex is per-file rather than in an engine
// module (IPAM is deliberately not a registered engine).
const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

// FIVE STEPS. The host account is its own step and not a field on the accounts
// step, because it is the one choice on this screen that is immovable, that
// decides who pays, and that can be refused outright by a quota AWS will never
// raise.
const steps = ['Host account', 'Accounts', 'Regions', 'Scope & cost', 'Review'];
export const HOST_STEP = 0;
export const ACCOUNTS_STEP = 1;
export const REGIONS_STEP = 2;
export const SCOPE_STEP = 3;
export const REVIEW_STEP = 4;

const DEFAULT_HOME_REGION = 'us-east-1';

// --- The two AWS quotas this wizard has to talk about, and they are opposites ---

// "Resource discovery associations per IPAM" - default 5, ADJUSTABLE. One of the
// five is consumed by the host's own default resource discovery at CreateIpam, so
// the default member ceiling is four (DD-39).
const ASSOCIATION_QUOTA_CODE = 'L-037D1B6C';
const ASSOCIATION_QUOTA_SERVICE = 'ec2-ipam';

// "IPAMs per Region" - value 1, and NOT ADJUSTABLE. No request raises it and no
// approval unblocks it, which is why a host-account collision is a distinct,
// terminal outcome on the host step and never a generic failure to retry.
const IPAM_PER_REGION_QUOTA_CODE = 'L-F8B4A9E6';

// services.ipamAssociationBudget. USED ONLY WHEN THE LIVE VALUE CANNOT BE READ,
// and the UI says so when it falls back: "your limit is 4" and "we could not read
// your limit and are assuming 4" call for different copy and different remedies.
//
// This fallback used to be what EVERY first-time enrolment saw, because the quota
// routes needed a config row and this screen runs before one exists — so a
// customer who had already raised their ceiling was capped at four members on
// their first enable, and the button that files the increase was disabled exactly
// when it was needed. GET /ipam/preflight and the host+region parameters on the
// quota routes are what fixed that; this constant is now genuinely the last
// resort, and `ceilingIsLive` is what says which of the two is on screen.
//
// The member ceiling is derived here from the same expression the backend uses
// (ceiling - 1, the -1 being the host's own default association) so the wizard's
// warning and the server's refusal cannot disagree by one.
const IPAM_DEFAULT_ASSOCIATION_CEILING = 5;
const memberCeilingFor = (ceiling) => ceiling - 1;

// --- The four host-IPAM dispositions, and the fifth state that is not one ---
//
// The backend classifies the host account's existing IPAM (if any) into exactly
// one of these, and each one means something different to the operator standing
// in front of the consent switch. They are spelled as constants because three of
// the four change what the wizard is allowed to do next.
const HOST_IPAM_NONE = 'none';
const HOST_IPAM_OURS = 'ours';
const HOST_IPAM_ADOPTABLE = 'adoptable';
const HOST_IPAM_BLOCKING = 'blocking';

// THE FIFTH STATE IS "PRISM DOES NOT KNOW", and it is the one this screen has to
// work hardest not to collapse into "none". `hostIpamChecked: false` means the
// DescribeIpams call FAILED — a denied permission, a throttle, an unreachable
// Region — and an unread account is not an empty one. Because L-F8B4A9E6 ("IPAMs
// per Region") is 1 and is NOT adjustable, a false all-clear here does not cost a
// retry: it walks the operator through cost consent and into a CreateIpam that
// AWS refuses outright, at the one point in the flow where there is no remedy
// left. So the unknown gets its own panel, its own words and its own severity,
// and it is never silently folded into the clean case.
const HOST_IPAM_UNKNOWN = 'unknown';

// 30 s, not the shared 3 s POLL_INTERVAL_MS. This poll reaches AWS Service Quotas
// through Prism, and it is waiting on a human at AWS working a support case -
// three-second polling would be thousands of pointless calls against a wait
// measured in hours.
const QUOTA_POLL_MS = 30000;

// The estimate is computed by a background goroutine that grants each account
// first and only then counts attached-ENI IPs, so it lands seconds to minutes
// after the draft does. 5 s x 120 bounds the wait at ten minutes - the same
// budget the per-account grant gate uses - after which the screen says "not
// estimated" and offers a manual re-check rather than spinning forever.
const ESTIMATE_POLL_MS = 5000;
const MAX_ESTIMATE_POLLS = 120;

// Three consecutive failures stop either poll. A poll that retries forever against
// a dead backend renders as a screen that is still "working".
const MAX_POLL_FAILURES = 3;

const ipamButtonSx = {
  backgroundColor: IPAM_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: IPAM_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const quietButtonSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  color: CK.body,
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const captionTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };
const monoTextSx = { fontFamily: FONT_MONO, fontSize: '0.8125rem', color: CK.navy, wordBreak: 'break-word' };

// AWS's RequestedServiceQuotaChange status vocabulary, verbatim.
//
// TWO OF THESE ARE TERMINAL AND MUST NOT READ AS "TRY AGAIN". A denied or closed
// request is not a slow one; the operator's next action is a human escalation with
// AWS, not another click here, and Next stays blocked either way.
//
// APPROVED IS NOT AN UNBLOCK EITHER, and that is the whole reason this map exists
// rather than a formatStatusLabel call: AWS moves a request to APPROVED before the
// applied value actually changes, so a gate that opened on this string would wave
// the operator into a half-enrolment with the per-active-IP meter already running.
const QUOTA_REQUEST_META = {
  PENDING: {
    intent: 'info',
    label: 'Pending',
    hint: 'AWS has the request and has not opened a support case for it yet.',
  },
  CASE_OPENED: {
    intent: 'info',
    label: 'Case opened',
    hint: 'AWS opened a support case on this account. A human at AWS decides it; there is nothing '
      + 'further to do here.',
  },
  APPROVED: {
    intent: 'success',
    label: 'Approved',
    hint: 'AWS approved the request. The applied limit normally follows within minutes. Prism '
      + 're-reads the applied limit itself and opens this step when it covers your selection - '
      + 'approval on its own does not.',
  },
  DENIED: {
    intent: 'error',
    label: 'Denied',
    terminal: true,
    hint: 'AWS declined the increase. Prism cannot re-file it into a decision that has already been '
      + 'made - take it up with AWS Support on this account, or enrol fewer accounts.',
  },
  CASE_CLOSED: {
    intent: 'error',
    label: 'Case closed',
    terminal: true,
    hint: 'AWS closed the support case without raising the limit. Prism cannot reopen it - take it up '
      + 'with AWS Support on this account, or enrol fewer accounts.',
  },
};

// parseOperatingRegions reads the config row's operatingRegions, which is stored
// and returned as a JSON-encoded STRING (`gorm:"type:text"`), not as an array. A
// caller that spread it straight into a picker would get a list of characters.
function parseOperatingRegions(config) {
  const home = config?.homeRegion || DEFAULT_HOME_REGION;
  const raw = config?.operatingRegions;
  let parsed = [];
  if (Array.isArray(raw)) {
    parsed = raw;
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const decoded = JSON.parse(raw);
      if (Array.isArray(decoded)) parsed = decoded;
    } catch (_) {
      // A malformed column is not worth an error card on a wizard the operator can
      // simply re-answer; fall through to the home Region, which the backend
      // force-prepends anyway.
      parsed = [];
    }
  }
  const list = parsed.filter((r) => typeof r === 'string' && r.trim() !== '');
  return list.includes(home) ? list : [home, ...list];
}

// readEstimatedActiveIps returns null - never 0 - for "not estimated".
//
// -1 IS THE SERVICE'S SENTINEL AND 0 IS A REAL, DIFFERENT ANSWER. Collapsing the
// two is how an un-estimated bill renders as "$0.00", which reads as "free" on the
// one screen where that misreading costs the customer money.
function readEstimatedActiveIps(row) {
  const raw = row?.estimatedActiveIps;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

// classifyBeginFailure sorts the three refusals that have different remedies.
//
// portalFetch DISCARDS the response body, so the structured `quota` object the
// handler attaches to a refusal never reaches this file - the message string is
// all there is. It is matched on the AWS QUOTA CODES rather than on English
// because those are stable identifiers that appear in both messages verbatim,
// while the prose around them is not a contract.
function classifyBeginFailure(message) {
  const text = String(message || '');
  if (text.includes(IPAM_PER_REGION_QUOTA_CODE) || /one ipam per region|one per region/i.test(text)) {
    return 'host_ipam_conflict';
  }
  if (text.includes(ASSOCIATION_QUOTA_CODE) || /resource-discovery associations/i.test(text)) {
    return 'association_quota';
  }
  return 'other';
}

// readHostDisposition collapses the pre-flight payload into the one word this
// screen branches on, and it FAILS TOWARDS UNKNOWN in every ambiguous case.
//
// Three things reach here as the same answer and they are all "Prism cannot
// vouch for this account": no pre-flight yet, a pre-flight whose hostIpamChecked
// is false, and a disposition string Prism does not recognise. The third matters
// as much as the second - AWS vocabularies grow, and a value that fell through to
// "no panel at all" would render an unclassified IPAM as a clean account, which
// is exactly the false all-clear the check exists to prevent.
function readHostDisposition(preflight) {
  if (!preflight || preflight.hostIpamChecked !== true) return HOST_IPAM_UNKNOWN;
  const d = String(preflight.hostIpamDisposition || '');
  if (d === HOST_IPAM_NONE || d === HOST_IPAM_OURS
    || d === HOST_IPAM_ADOPTABLE || d === HOST_IPAM_BLOCKING) return d;
  return HOST_IPAM_UNKNOWN;
}

// readHostRemedies keeps only real strings. The backend writes these sentences
// itself (services.ipamHostConflictRemedies) and they are rendered VERBATIM: they
// name the exact IPAM id and the exact next action, and a paraphrase is how "Prism
// will not spend a customer's money on an IPAM it does not own" becomes "upgrade
// your IPAM".
function readHostRemedies(preflight) {
  const raw = preflight?.hostIpamRemedies;
  if (!Array.isArray(raw)) return [];
  return raw.filter((r) => typeof r === 'string' && r.trim() !== '');
}

// The backend's own sentences, as a list. No numbering and no re-ordering: they
// arrive in the order the service put them in, which is the order it considers
// them worth trying.
function RemedyList({ remedies, heading }) {
  if (!remedies.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      {heading && (
        <Typography sx={{ ...bodyTextSx, color: 'inherit', fontWeight: 600, mb: 0.5 }}>
          {heading}
        </Typography>
      )}
      <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0 }}>
        {remedies.map((r) => (
          <Typography component="li" key={r} sx={{ ...bodyTextSx, color: 'inherit' }}>
            {r}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// The existing IPAM's identity, drawn as data rather than folded into prose. Tier
// and state are what decide adoptable from blocking on the server, so an operator
// arguing with the verdict needs to see the same three values it read.
function HostIpamFacts({ ipamId, tier, state }) {
  if (!ipamId && !tier && !state) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
      {ipamId && <Typography sx={monoTextSx}>{ipamId}</Typography>}
      {tier && <Chip size="small" label={`tier ${tier}`} sx={statusChipSx('neutral')} />}
      {state && <Chip size="small" label={state} sx={statusChipSx('neutral')} />}
    </Stack>
  );
}

// One row of the Review step - a fixed label column plus the chosen value, so
// every row shares a single baseline grid.
function ReviewRow({ label, value }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} sx={{ py: 0.5 }}>
      <Typography sx={{ minWidth: 180, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// The unavoidability alert. Rendered on the cost step AND restated on Review,
// because the price has to be the first thing on the screen that asks for consent
// and the last thing on the screen that takes it.
//
// Every string here comes from ipamPricing.js. The rate is never re-spelled in a
// page: the wizard and the Settings tier tab are the only two screens that can put
// a tenant on the paid tier, and a price that disagrees with itself across them is
// a support ticket at best.
function AdvancedTierAlert({ sx }) {
  return (
    <Alert severity="warning" sx={sx}>
      <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
        VPC IPAM Advanced tier is required, and it is billed — {IPAM_ADVANCED_PRICE}
      </AlertTitle>
      {IPAM_ADVANCED_REQUIRED_NOTE}
      <br /><br />
      {IPAM_FREE_TIER_NOTE}
      <br /><br />
      That is {IPAM_ADVANCED_PRICE_MONTHLY}. {IPAM_PRICE_NOTE}
    </Alert>
  );
}

// IpamSetup is the enrolment wizard AND the cost gate.
//
// IT IS A TWO-STAGE FLOW, and the split is the point. POST /config BEGINS: it
// writes a draft at awaiting_consent, attaches read permissions and counts
// attached-ENI IPs, and creates NO AWS RESOURCE - so the cost screen can show a
// real number before anyone agrees to anything. POST /config/activate is the
// second stage and carries `costAcknowledged`, which THE BACKEND REFUSES WITHOUT.
// The switch on the cost step is the honest presentation of that server-side
// refusal, not a client-side conscience: a checkbox that only gated a button
// would be unprovable six months later, when the first bill is disputed.
//
// `startAt` and `config` are how IpamEntry resumes a draft that is already sitting
// at awaiting_consent - a tenant can legitimately stay there for days.
export default function IpamSetup({ startAt = HOST_STEP, config = null, priorFailure = null, onComplete }) {
  const [active, setActive] = useState(startAt);
  const [accounts, setAccounts] = useState([]);

  // Form state - exactly the four fields of services.IpamConfigRequest. Seeded
  // from the draft when one is being resumed; IpamEntry gives the two <IpamSetup>
  // branches different keys, so a resume always arrives as a fresh mount and these
  // initialisers always run.
  const [hostAccountId, setHostAccountId] = useState(config?.hostAccountId || '');
  const [homeRegion, setHomeRegion] = useState(config?.homeRegion || DEFAULT_HOME_REGION);
  const [operatingRegions, setOperatingRegions] = useState(() => parseOperatingRegions(config));
  const [memberAccountIds, setMemberAccountIds] = useState([]);

  // THE GATE ALWAYS STARTS OFF, even when the draft being resumed already carries
  // an acknowledgement. Consent is for a specific set of accounts and Regions at a
  // specific estimated size - the backend clears it on every re-begin for exactly
  // that reason - and a switch that arrives pre-checked is what makes a gate
  // ceremonial.
  const [costAcknowledged, setCostAcknowledged] = useState(false);

  const [draft, setDraft] = useState(config);
  const [costPreview, setCostPreview] = useState(null);
  const [estimateGaveUp, setEstimateGaveUp] = useState(false);

  // The host pre-flight (GET /ipam/preflight), read as soon as BOTH the host
  // account and the home Region are chosen - the quota is per (account, Region)
  // and so is "IPAMs per Region", so neither question has an answer until both
  // halves exist.
  const [preflight, setPreflight] = useState(null);
  const [preflightError, setPreflightError] = useState(null);
  const [preflightChecking, setPreflightChecking] = useState(false);
  // Monotonic sequence, not just a cancelled flag. Changing the Region re-fires
  // this read, and two in flight can land out of order - a stale "no IPAM here"
  // arriving after a fresh "blocking" is the false all-clear, written by a race.
  const preflightSeqRef = useRef(0);

  const [quota, setQuota] = useState(null);
  // Whether the quota read answered at all. It no longer needs a config row: the
  // three quota routes take the host account and Region as parameters and consult
  // them until a config exists, so this screen can read the real ceiling and file
  // a real increase on a tenant enrolling for the first time. False here now means
  // the read genuinely failed, not that it was structurally impossible.
  const [quotaReadable, setQuotaReadable] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const [quotaBusy, setQuotaBusy] = useState(false);

  const [beginning, setBeginning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hostConflict, setHostConflict] = useState(null);
  const [quotaRefusal, setQuotaRefusal] = useState(null);

  const configId = config?.id || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSecurityHubAccounts();
        if (cancelled) return;
        setAccounts(res?.data?.accounts || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resuming a draft: the member list is NOT on the config row, it is the
  // enrolment grid. Tombstoned cells are filtered out - a `disabled` row is the
  // record that Prism once attached a policy to that account's switch role, not a
  // member of this enrolment - and so is the host's own row, which is a real cell
  // but must never appear in memberAccountIds.
  useEffect(() => {
    if (!configId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await listIpamMembers();
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setMemberAccountIds(
          rows.filter((r) => r.role !== 'host' && r.phase !== 'disabled').map((r) => r.accountId)
        );
      } catch (_) {
        // Not fatal and not worth an error card: the operator can re-pick the
        // accounts, and the ceiling gate below re-validates whatever they pick.
      }
    })();
    return () => { cancelled = true; };
  }, [configId]);

  // --- The host pre-flight (DD-39 step a, and guard 8 brought forward) -----

  // Both halves are required and neither is a default: the association ceiling is
  // per account AND per Region, and "IPAMs per Region" is meaningless without a
  // Region. Firing on the account alone would answer about the wrong Region and
  // answer confidently.
  useEffect(() => {
    if (!hostAccountId || !homeRegion) {
      setPreflight(null);
      setPreflightError(null);
      setPreflightChecking(false);
      return undefined;
    }
    const seq = preflightSeqRef.current + 1;
    preflightSeqRef.current = seq;
    let cancelled = false;
    // The previous answer is DISCARDED before the new one is asked for, not left
    // on screen underneath a spinner. It was a verdict about a different account
    // or a different Region, and a stale "no IPAM here" is precisely the sentence
    // that must never appear next to a host it was not about.
    setPreflight(null);
    setPreflightError(null);
    setPreflightChecking(true);
    (async () => {
      try {
        const res = await getIpamHostPreflight({ hostAccountId, region: homeRegion });
        if (cancelled || seq !== preflightSeqRef.current) return;
        setPreflight(res?.data || null);
        setPreflightError(res?.data ? null : 'The pre-flight check returned no result.');
      } catch (e) {
        if (cancelled || seq !== preflightSeqRef.current) return;
        setPreflight(null);
        setPreflightError(e?.message || 'Prism could not run the host pre-flight check.');
      } finally {
        if (!cancelled && seq === preflightSeqRef.current) setPreflightChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hostAccountId, homeRegion]);

  const hostDisposition = readHostDisposition(preflight);
  const hostRemedies = readHostRemedies(preflight);
  const hostIpamId = preflight?.hostIpamId || '';
  // Terminal. This is the one outcome no quota request and no retry resolves, so
  // it is also the one that must never reach CreateIpam.
  const hostBlocking = hostDisposition === HOST_IPAM_BLOCKING;
  const hostWillAdopt = hostDisposition === HOST_IPAM_ADOPTABLE;
  // "Prism cannot vouch for this account" - distinct from every clean answer, and
  // only asserted once a host and Region have actually been chosen and asked about.
  const hostUnknown = !!hostAccountId && !!homeRegion && !preflightChecking
    && hostDisposition === HOST_IPAM_UNKNOWN;

  // The adopt path in TWO TENSES, and they must not collapse. `draft.ipamAdopted`
  // is the PAST tense: a previous run already adopted the customer's IPAM. The
  // pre-flight is the FUTURE tense: this run will, if consent is given. Only the
  // second one can be said BEFORE the click, and the whole reason the pre-flight
  // exists is that saying nothing before the click made adoption something
  // customers found out about from a bill.
  //
  // Either way "Prism creates the IPAM here" and "the bill lands on a resource
  // Prism owns" are both false: the meter is the customer's, on a resource Prism
  // will never delete, and what they are agreeing to is N more accounts on it.
  const adoptedAlready = draft?.ipamAdopted === true;
  const willAdopt = hostWillAdopt && !adoptedAlready;
  const adopted = adoptedAlready || willAdopt;
  const adoptedIpamId = draft?.ipamId || hostIpamId || '';

  // --- The association-ceiling gate (DD-39 step b) ------------------------

  // Host and Region travel on EVERY call from this wizard. The backend rule is
  // that the config row wins whenever there is one, so sending them post-begin is
  // harmless - and omitting them pre-begin is what used to leave this screen with
  // nothing to read and no button to press.
  const readQuota = useCallback(async ({ recheck = false } = {}) => {
    if (!hostAccountId || !homeRegion) return false;
    const params = { hostAccountId, region: homeRegion };
    try {
      const res = recheck ? await recheckIpamQuota(params) : await getIpamQuota(params);
      setQuota(res?.data || null);
      setQuotaReadable(true);
      setQuotaError(null);
      return true;
    } catch (e) {
      setQuotaError(e.message);
      return false;
    }
  }, [hostAccountId, homeRegion]);

  // Keyed on readQuota alone, which is itself keyed on the host and the Region:
  // choosing a different host re-reads a quota that belongs to a different
  // account. A stale ceiling is the same class of bug as a stale disposition.
  useEffect(() => {
    setQuota(null);
    setQuotaReadable(false);
    setQuotaError(null);
    readQuota();
  }, [readQuota]);

  // quotaRow is whichever payload currently carries the ceiling and the tracked
  // request. Both spell the same three request fields; they differ only in the
  // name of the number (`appliedCeiling` on services.IpamQuotaStatus,
  // `associationCeiling` on the pre-flight), and the backend resolves both by the
  // same rule. GET /ipam/quota is preferred once it has answered because it is the
  // one this screen re-polls while the operator waits on AWS.
  const quotaRow = (quotaReadable && quota) || preflight || null;
  const readCeiling = Number(quotaRow?.appliedCeiling ?? quotaRow?.associationCeiling ?? 0);

  // ceilingIsLive IS A CLAIM ABOUT THE NUMBER ON SCREEN, so it is false whenever
  // the number on screen is the fallback - including the contradictory case of a
  // payload that claims a live reading but carries no value. A fallback presented
  // as a reading is worse than no number: it is a limit the customer will believe.
  const ceiling = readCeiling > 0 ? readCeiling : IPAM_DEFAULT_ASSOCIATION_CEILING;
  const ceilingIsLive = readCeiling > 0 && quotaRow?.ceilingIsLive === true;
  const memberCeiling = memberCeilingFor(ceiling);
  const selectedCount = memberAccountIds.length;
  const overCeiling = selectedCount > memberCeiling;
  // len(members) + 1. The +1 is the host's own default association, and this is
  // the exact number that will be filed - so it is the number the operator is
  // shown before they approve it, never one recomputed afterwards.
  const desiredValue = selectedCount + 1;

  const quotaRequestStatus = quotaRow?.requestStatus || '';
  // A status Prism does not recognise is rendered AS ITSELF, never dropped. AWS's
  // enum is these five today, but a value that fell through to "no narrative at
  // all" would render a filed request as though none existed - an unknown state
  // reading as a clean one, on the gate that decides whether enrolment proceeds.
  const quotaRequestMeta = quotaRequestStatus
    ? (QUOTA_REQUEST_META[quotaRequestStatus] || {
      intent: 'neutral',
      label: quotaRequestStatus,
      hint: 'Prism does not recognise this request status. Check the request in the host account’s '
        + 'Service Quotas console — enrolment still opens only when the applied limit covers your '
        + 'selection.',
    })
    : null;
  const quotaRequestTerminal = quotaRequestMeta?.terminal === true;
  const quotaRequestOpen = !!quotaRequestStatus && !quotaRequestTerminal;

  // Prism detects approval ITSELF by re-reading the applied ceiling; the "Re-check
  // now" button below is a convenience and never the source of truth. A customer
  // asserting "it has been raised" was rejected as a signal outright - a wrong
  // assertion half-enrols with the meter already running.
  const shouldPollQuota = active === ACCOUNTS_STEP && quotaReadable && overCeiling && !quotaRequestTerminal;

  useEffect(() => {
    if (!shouldPollQuota) return undefined;
    let failures = 0;
    const id = setInterval(async () => {
      const ok = await readQuota();
      failures = ok ? 0 : failures + 1;
      if (failures >= MAX_POLL_FAILURES) clearInterval(id);
    }, QUOTA_POLL_MS);
    return () => clearInterval(id);
  }, [shouldPollQuota, readQuota]);

  // Files a REAL AWS Support case on the customer's own account, which is why it
  // is never automatic and never a side effect of arriving on this step. Host and
  // Region go in the body so the route can be served before a config row exists -
  // this is the whole point of DD-39 step c, and it used to be unreachable exactly
  // when it was needed.
  const requestQuota = async () => {
    setQuotaBusy(true);
    setQuotaError(null);
    try {
      const res = await requestIpamQuotaIncrease(desiredValue, {
        hostAccountId,
        region: homeRegion,
      });
      setQuota(res?.data || null);
      setQuotaReadable(true);
    } catch (e) {
      setQuotaError(e.message);
    } finally {
      setQuotaBusy(false);
    }
  };

  const recheckQuota = async () => {
    setQuotaBusy(true);
    setQuotaError(null);
    await readQuota({ recheck: true });
    setQuotaBusy(false);
  };

  // --- The estimate ------------------------------------------------------

  const reloadEstimate = useCallback(async () => {
    let ok = false;
    try {
      const res = await getIpamConfig();
      if (res?.data) setDraft(res.data);
      ok = true;
    } catch (_) {
      // The cost preview below may still answer; a failure here is counted by the
      // caller, not reported as an error card over a screen that is mid-wait.
    }
    try {
      const res = await getIpamCostPreview();
      // `data: null` means "nothing to estimate yet", which is a real answer and
      // not a fault. The route is also one of the handlers still answering 501
      // today, which reaches here as a rejection - deliberately not special-cased,
      // because the draft config row carries the same estimate and the screen
      // lights up on its own the day the route lands.
      setCostPreview(res?.data || null);
      ok = true;
    } catch (_) {
      // Same reasoning.
    }
    return ok;
  }, []);

  const activeIps = readEstimatedActiveIps(costPreview) ?? readEstimatedActiveIps(draft);
  const monthlyCost = formatMonthlyCostUsd(activeIps);
  const estimateReady = activeIps !== null;
  const draftAwaitingConsent = draft?.status === 'awaiting_consent';
  const shouldPollEstimate = active === SCOPE_STEP && draftAwaitingConsent && !estimateReady && !estimateGaveUp;

  // The condition is a BOOLEAN, so the interval is built once and torn down once:
  // it survives every draft update the poll itself causes, and is cleared the tick
  // the estimate lands. Keying this on the draft object instead would rebuild the
  // timer on every response and drift the cadence to ESTIMATE_POLL_MS + RTT.
  useEffect(() => {
    if (!shouldPollEstimate) return undefined;
    let attempts = 0;
    let failures = 0;
    const id = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ESTIMATE_POLLS) {
        clearInterval(id);
        setEstimateGaveUp(true);
        return;
      }
      const ok = await reloadEstimate();
      failures = ok ? 0 : failures + 1;
      if (failures >= MAX_POLL_FAILURES) {
        clearInterval(id);
        setEstimateGaveUp(true);
      }
    }, ESTIMATE_POLL_MS);
    return () => clearInterval(id);
  }, [shouldPollEstimate, reloadEstimate]);

  // --- Navigation and validation -----------------------------------------

  const accountLabel = (id) => {
    const acct = accounts.find((a) => a.accountId === id);
    return acct ? `${acct.name || acct.accountId} (${acct.accountId})` : id;
  };
  const hostAccountLabel = hostAccountId ? accountLabel(hostAccountId) : 'the host account';

  const stepComplete = (step) => {
    // THE PRE-FLIGHT IS PART OF THIS STEP'S COMPLETENESS, not decoration beside
    // it. `hostBlocking` is terminal - no quota raises L-F8B4A9E6 and no retry
    // clears it - so Next stays shut rather than letting the operator spend four
    // more screens and a cost acknowledgement on an enrolment AWS will refuse at
    // CreateIpam. The check being IN FLIGHT is also not complete: a Next pressed
    // before the answer lands is a Next pressed on no answer at all.
    //
    // An UNKNOWN result deliberately does NOT block. The read failing is not
    // evidence of a conflict either, and the authoritative gate is server-side:
    // POST /config re-runs this check and REFUSES outright when it cannot read the
    // account, before any consent is collected. Blocking here as well would strand
    // every tenant behind a transient throttle with nothing to press. What the
    // unknown does instead is say so, in its own words, on this screen.
    if (step === HOST_STEP) {
      return !!hostAccountId && !!homeRegion && !hostConflict && !hostBlocking && !preflightChecking;
    }
    // Next is disabled AT the boundary rather than letting the operator reach a
    // submit-time refusal, and the threshold is the same expression the backend
    // uses. The host is not in memberAccountIds, so there is no off-by-one beyond
    // the -1 for its own default association.
    if (step === ACCOUNTS_STEP) return selectedCount > 0 && !overCeiling;
    if (step === REGIONS_STEP) return operatingRegions.length > 0 && operatingRegions.includes(homeRegion);
    // THE COST GATE.
    if (step === SCOPE_STEP) return costAcknowledged;
    return true;
  };

  // canSubmit RE-CHECKS EVERY STEP rather than trusting that the operator walked
  // through them in order. Toggling the gate on, pressing Back and editing the
  // account list has to route through the cost step again - the estimate, and the
  // sentence the switch agrees to, are both about a specific enrolment.
  const canSubmit = stepComplete(HOST_STEP)
    && stepComplete(ACCOUNTS_STEP)
    && stepComplete(REGIONS_STEP)
    && stepComplete(SCOPE_STEP);

  const chooseHost = (value) => {
    setHostAccountId(value);
    setHostConflict(null);
    // The host is a separate field and must never also be a member.
    setMemberAccountIds((prev) => prev.filter((m) => m !== value));
  };

  const chooseHomeRegion = (value) => {
    setHomeRegion(value);
    setHostConflict(null);
    setOperatingRegions((prev) => (prev.includes(value) ? prev : [value, ...prev]));
  };

  const begin = async () => {
    setBeginning(true);
    setError(null);
    setHostConflict(null);
    setQuotaRefusal(null);
    try {
      const res = await beginIpamConfig({
        hostAccountId,
        memberAccountIds,
        homeRegion,
        operatingRegions,
      });
      const row = res?.data;
      // Without this guard a 2xx carrying no draft would leave the wizard on the
      // Regions step with the button re-enabled and nothing said.
      if (!row) throw new Error('VPC IPAM was not prepared — the server returned no draft configuration.');
      setDraft(row);
      setCostPreview(null);
      setEstimateGaveUp(false);
      setCostAcknowledged(false);
      setActive(SCOPE_STEP);
      // The draft now exists, which is also what makes the quota routes answerable.
      readQuota();
      // Let IpamEntry catch up: it owns the status narration, and a re-begin from
      // the cost step needs it to see awaiting_consent again.
      if (onComplete) onComplete();
    } catch (e) {
      const kind = classifyBeginFailure(e?.message);
      if (kind === 'host_ipam_conflict') {
        // No quota request helps and there is no remedy later, so the operator goes
        // back to the one choice that can still change the outcome.
        setHostConflict(e.message);
        setActive(HOST_STEP);
      } else if (kind === 'association_quota') {
        setQuotaRefusal(e.message);
        setActive(ACCOUNTS_STEP);
        readQuota();
      } else {
        setError(e.message);
      }
    } finally {
      setBeginning(false);
    }
  };

  const next = () => {
    setError(null);
    if (active === REGIONS_STEP) {
      // Leaving the Regions step is what creates (or re-creates) the draft, because
      // the estimate on the next screen is only meaningful for THIS set of accounts
      // and Regions. Re-beginning also clears the previous acknowledgement
      // server-side, which is what stops consent for three accounts being reused to
      // enable thirty.
      begin();
      return;
    }
    setActive((s) => s + 1);
  };

  const back = () => {
    setError(null);
    setActive((s) => Math.max(HOST_STEP, s - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // costAcknowledged is sent as what the operator actually did, never as a
      // hard-coded true. The service enforces the gate first, before the status
      // check and before any write, so a false here leaves the tenant not paying.
      await activateIpamConfig(costAcknowledged);
      // Deliberately left submitting: IpamEntry replaces this wizard with the
      // enable narration on its next read, and re-enabling the button in that gap
      // invites a second activate against a config that is no longer awaiting
      // consent.
      if (onComplete) onComplete();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  // --- Step bodies --------------------------------------------------------

  // The pre-flight verdict, rendered FIVE ways because there are five different
  // things it can mean and only one of them is "carry on". They are told apart by
  // severity, by title and by the action each offers - a customer's own adoptable
  // IPAM and a failed read must not be a single grey "note".
  const hostPreflightPanel = () => {
    if (!hostAccountId || !homeRegion) return null;

    if (preflightChecking) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            Checking {hostAccountLabel} for an existing IPAM in {homeRegion}…
          </AlertTitle>
          Prism is reading this account&apos;s IPAMs and its resource-discovery association limit.
          Nothing is created and nothing is billed by this check.
        </Alert>
      );
    }

    if (hostDisposition === HOST_IPAM_NONE) {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            No IPAM in {homeRegion} — Prism will create one
          </AlertTitle>
          Prism read this account and found no IPAM in {homeRegion}. It will create one here, own it,
          and delete it if you turn VPC IPAM off.
        </Alert>
      );
    }

    if (hostDisposition === HOST_IPAM_OURS) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            Prism already owns the IPAM in this account
          </AlertTitle>
          The IPAM in {homeRegion} was created by Prism for this tenant, so this is <b>not</b> a
          conflict. Enrolling continues onto that same IPAM rather than creating a second one, which
          AWS would refuse.
          <HostIpamFacts
            ipamId={hostIpamId}
            tier={preflight?.hostIpamTier}
            state={preflight?.hostIpamState}
          />
        </Alert>
      );
    }

    // ADOPTION CHANGES WHAT CONSENT MEANS, which is why it is stated here, before
    // the accounts are even picked, and again immediately above the switch that
    // takes the acknowledgement. It is not a footnote: the customer is agreeing to
    // put N member discoveries onto a meter attached to a resource of their own
    // that Prism will never delete.
    if (hostWillAdopt) {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            Prism will adopt the IPAM that already exists here — it will not create one
          </AlertTitle>
          Account {hostAccountId} already owns an IPAM in {homeRegion}, and AWS allows exactly one
          per Region. Prism will <b>adopt {hostIpamId || 'it'}</b> rather than create its own.
          <br /><br />
          <b>Prism will never delete it.</b> Turning VPC IPAM off later removes Prism&apos;s own
          associations and leaves this IPAM — and its meter — running exactly as it found them.
          <br /><br />
          Every member account you enrol has its resource discovery associated <b>into your IPAM</b>,
          so the active IPs they discover are metered on the account that owns it — <b>this account,
          on a resource Prism does not control the lifecycle of</b>. That is a charge on your own
          bill, not a new resource of Prism&apos;s.
          <HostIpamFacts
            ipamId={hostIpamId}
            tier={preflight?.hostIpamTier}
            state={preflight?.hostIpamState}
          />
          <RemedyList remedies={hostRemedies} heading="Your options:" />
        </Alert>
      );
    }

    if (hostBlocking) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            Prism cannot use the IPAM this account already has in {homeRegion}
          </AlertTitle>
          Account {hostAccountId} already owns an IPAM in {homeRegion} that Prism can neither create
          alongside nor adopt. <b>No quota increase resolves this and no retry clears it</b>:
          &quot;IPAMs per Region&quot; is {IPAM_PER_REGION_QUOTA_CODE}, its value is 1, and AWS does
          not adjust it.
          <HostIpamFacts
            ipamId={hostIpamId}
            tier={preflight?.hostIpamTier}
            state={preflight?.hostIpamState}
          />
          <RemedyList remedies={hostRemedies} heading="What resolves this:" />
          <Typography sx={{ ...bodyTextSx, color: 'inherit', mt: 1.5 }}>
            Change the host account or the home Region below and Prism will check again. Nothing has
            been created and nothing is billing.
          </Typography>
        </Alert>
      );
    }

    // UNKNOWN. Not an all-clear, and it does not get to look like one.
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          Prism could not check this account for an existing IPAM
        </AlertTitle>
        The check on account {hostAccountId} in {homeRegion} did not complete, so <b>Prism does not
        know</b> whether an IPAM already exists there. <b>This is not an all-clear.</b> AWS allows
        exactly one IPAM per Region ({IPAM_PER_REGION_QUOTA_CODE}, value 1, not adjustable), so if
        one does exist, creating another is refused outright.
        <br /><br />
        You can continue. Prism attempts this check again when you press <b>Continue to cost</b>,
        but it does not gate the acknowledgement — you can be asked to accept the cost while this
        account is still unread. The check that counts runs later: Prism <b>re-checks before
        enabling</b>, with its access to the account in place, and refuses before creating the
        IPAM. <b>Nothing is created</b> before that check succeeds, and the meter starts with the
        IPAM.
        {preflightError && (
          <Typography sx={{ ...captionTextSx, color: 'inherit', mt: 1.5 }}>
            {preflightError}
          </Typography>
        )}
      </Alert>
    );
  };

  const hostStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography sx={{ ...bodyTextSx, mb: 2 }}>
        The host account owns the IPAM. Every other account you enrol shares its resource
        discovery back to this one through AWS RAM, and this is where the address-space
        inventory is assembled.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          This account is not an AWS delegated administrator
        </AlertTitle>
        Prism creates the IPAM here and asks every other enrolled account to share its resource
        discovery with it through AWS RAM. No management-account access is used, no AWS
        Organizations setting is changed, and no delegated administrator is registered. This
        account is where the IPAM lives — and by default it is <b>where the IPAM bill for every
        account you enrol lands</b>. Only the accounts you enrol are managed and billed; no other
        account in your organization is affected.
      </Alert>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          The IPAM cannot be moved later
        </AlertTitle>
        Moving the IPAM to another account or Region means deleting this one, and deleting an IPAM
        destroys every CIDR allocation record it holds.
        {' '}All CIDR address history is destroyed, irreversibly.
        {' '}<b>Choose an account you will keep.</b>
        <br /><br />
        AWS also allows exactly one IPAM per Region and that limit ({IPAM_PER_REGION_QUOTA_CODE},
        value 1) <b>cannot be raised by a quota request</b>. If this account already has an IPAM in
        the Region below, Prism adopts it where AWS allows that and refuses where it does not —
        in either case before anything is created.
      </Alert>

      {hostPreflightPanel()}

      {hostConflict && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            This account already has an IPAM in {homeRegion}
          </AlertTitle>
          {hostConflict}
          <br /><br />
          <b>No quota increase resolves this.</b> &quot;IPAMs per Region&quot; is{' '}
          {IPAM_PER_REGION_QUOTA_CODE}, its value is 1 and AWS does not adjust it. Choose a
          different host account or a different home Region above, then continue.
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Host account</InputLabel>
        <Select
          value={hostAccountId}
          label="Host account"
          onChange={(e) => chooseHost(e.target.value)}
        >
          {accounts.map((a) => (
            <MenuItem key={a.accountId} value={a.accountId}>
              {a.name || a.accountId} ({a.accountId})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Home Region</InputLabel>
        <Select
          value={homeRegion}
          label="Home Region"
          onChange={(e) => chooseHomeRegion(e.target.value)}
        >
          {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </Select>
      </FormControl>
      <Typography sx={{ ...captionTextSx, mt: 1 }}>
        The home Region is where the IPAM, its scopes and every resource-discovery association
        live. It is fixed for the life of the IPAM.
      </Typography>

      {accounts.length === 0 && (
        <Typography sx={{ ...captionTextSx, mt: 1 }}>
          No linked AWS accounts were returned for this organization.
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );

  const quotaGate = () => {
    if (!overCeiling) return null;
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          More accounts than your association limit allows
        </AlertTitle>
        Each enrolled account consumes one of the IPAM&apos;s resource-discovery association slots,
        and the host consumes one for itself the moment the IPAM is created. Enrolling{' '}
        {selectedCount} accounts needs an applied limit of <b>{desiredValue}</b>; the limit on
        account {hostAccountId || 'the host'} in {homeRegion} is <b>{ceiling}</b>
        {ceilingIsLive
          ? ''
          : ' — which Prism could not read and is assuming from the AWS default, so your real limit '
            + 'may already be higher'}.
        <br /><br />
        Prism can ask AWS to raise it, filing a request for exactly{' '}
        <b>{desiredValue} associations</b> against quota {ASSOCIATION_QUOTA_CODE} (
        {ASSOCIATION_QUOTA_SERVICE}). <b>Filing it opens a real AWS Support case on the customer&apos;s
        own account</b>, so Prism never files one without this click.

        {quotaRequestMeta && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography sx={{ ...bodyTextSx, fontWeight: 600 }}>Increase request</Typography>
              <Chip size="small" label={quotaRequestMeta.label} sx={statusChipSx(quotaRequestMeta.intent)} />
              {quotaRow?.requestDesiredValue > 0 && (
                <Typography sx={captionTextSx}>
                  filed for {quotaRow.requestDesiredValue} associations
                </Typography>
              )}
            </Stack>
            <Typography sx={bodyTextSx}>{quotaRequestMeta.hint}</Typography>
          </Box>
        )}

        {!quotaRequestTerminal && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {/* BOTH BUTTONS GATE ON THE HOST AND REGION, NOT ON A SUCCESSFUL
                  READ. They used to require `quotaReadable`, which pre-config was
                  never true — so the one flow that files the increase was dead
                  exactly on the screen that needs it. Re-check is the recovery
                  from a failed read and must survive one; the request is filed
                  server-side against the same (account, Region) pair. */}
              {!quotaRequestOpen && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={quotaBusy || !hostAccountId || !homeRegion}
                  onClick={requestQuota}
                  sx={ipamButtonSx}
                >
                  {quotaBusy ? 'Filing…' : `Request an increase to ${desiredValue}`}
                </Button>
              )}
              <Button
                size="small"
                disabled={quotaBusy || !hostAccountId || !homeRegion}
                onClick={recheckQuota}
                sx={quietButtonSx}
              >
                {quotaBusy ? 'Checking…' : 'Re-check now'}
              </Button>
            </Stack>
            <Typography sx={{ ...captionTextSx, mt: 1 }}>
              Prism re-reads the applied limit by itself and opens this step the moment it covers
              your selection. Re-check now only asks sooner — it is a convenience, not what
              unblocks enrolment, and neither is anyone&apos;s confirmation that the limit was
              raised.
            </Typography>
            {/* This no longer says "not until a configuration exists". The quota
                routes take the host account and Region as parameters, so the
                request can be filed from the wizard on a tenant's first enable —
                which is the only moment it is ever needed. False here now means
                the read actually failed, and that is what it says. */}
            {!ceilingIsLive && (
              <Typography sx={{ ...captionTextSx, mt: 1 }}>
                Prism could not read the applied limit on account {hostAccountId || 'the host'} in{' '}
                {homeRegion}, so the figure above is the documented AWS default and not yours —{' '}
                <b>re-check before filing</b>, because an increase you already have is a support case
                nobody needs. You can also raise {ASSOCIATION_QUOTA_CODE} in the host account&apos;s
                own Service Quotas console, or enrol up to {memberCeiling} accounts now and add the
                rest from the Accounts page later.
              </Typography>
            )}
          </Box>
        )}

        {quotaError && <Alert severity="error" sx={{ mt: 2 }}>{quotaError}</Alert>}
      </Alert>
    );
  };

  const accountsStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography sx={{ ...bodyTextSx, mb: 2 }}>
        Pick the accounts to inventory. Each one gets a resource discovery of its own, shared back
        to {hostAccountLabel} through AWS RAM. <b>Only the accounts you enrol are discovered, and
        only they are metered</b> — no other account in your organization is read or billed. At
        least one account besides the host is required: a host-only enrolment has no cross-account
        inventory and would be billed at the Advanced tier for nothing.
      </Typography>

      <MultiSelectField
        label="Accounts"
        options={accounts
          .filter((a) => a.accountId !== hostAccountId)
          .map((a) => ({ value: a.accountId, label: `${a.name || a.accountId} (${a.accountId})` }))}
        value={memberAccountIds}
        onChange={setMemberAccountIds}
      />

      {/* A READING AND AN ASSUMPTION ARE NOT THE SAME SENTENCE. When Prism has the
          live applied ceiling it says so and names where it read it; when it does
          not, the number is labelled as the documented AWS default rather than as
          this customer's limit. Presenting the fallback as a reading is what used
          to cap a customer who had already raised their quota at four accounts,
          with nothing on screen to suggest the figure was invented. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1.5 }}
      >
        <Typography sx={bodyTextSx}>
          <b>{selectedCount} account{selectedCount === 1 ? '' : 's'} selected</b> ·{' '}
          {ceilingIsLive ? (
            <>your limit is <b>{memberCeiling}</b> member account{memberCeiling === 1 ? '' : 's'}.</>
          ) : (
            <>Prism is <b>assuming</b> a limit of {memberCeiling} member account
              {memberCeiling === 1 ? '' : 's'}.</>
          )}
        </Typography>
        <Chip
          size="small"
          label={ceilingIsLive ? 'Your applied limit' : 'Assumed — AWS default'}
          sx={statusChipSx(ceilingIsLive ? 'success' : 'warning')}
        />
      </Stack>
      <Typography sx={{ ...captionTextSx, mt: 0.5 }}>
        {ceilingIsLive
          ? `Read from quota ${ASSOCIATION_QUOTA_CODE} ("Resource discovery associations per IPAM") on `
            + `account ${hostAccountId || 'the host'} in ${homeRegion}: ${ceiling} associations, one of `
            + 'which the host consumes for its own resource discovery.'
          : `Prism could not read the applied limit on account ${hostAccountId || 'the host'} in `
            + `${homeRegion}, so this is the documented AWS default of ${ceiling} associations per IPAM `
            + '— not your account’s limit. One of them is consumed by the host’s own resource '
            + 'discovery. If your quota has already been raised, your real limit is higher than this.'}
      </Typography>

      {quotaRefusal && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            AWS refused the enrolment on the association limit
          </AlertTitle>
          {quotaRefusal}
        </Alert>
      )}

      {quotaGate()}

      {accounts.length === 0 && (
        <Typography sx={{ ...captionTextSx, mt: 1 }}>
          No linked AWS accounts were returned for this organization.
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );

  const regionsStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography sx={{ ...bodyTextSx, mb: 2 }}>
        Operating Regions are the Regions the IPAM monitors. Discovery only finds address space in
        the Regions on this list, and adding one later re-applies it across every enrolled
        account&apos;s discovery.
      </Typography>

      <MultiSelectField
        label="Operating Regions"
        options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
        value={operatingRegions}
        onChange={setOperatingRegions}
        lockedValues={homeRegion ? [homeRegion] : []}
      />

      {/* Four different Region rules live inside this one capability, and stating
          them anywhere but together is how they get applied by analogy - which is
          exactly the failure mode that makes IPAM more Region-fragile than any
          sibling, where Region was always a single axis. */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          Four different Regions do four different jobs here
        </AlertTitle>
        <b>1. The home Region ({homeRegion}).</b> Where the IPAM, its scopes and every
        resource-discovery association live. Fixed for the life of the IPAM.
        <br />
        <b>2. The operating Regions (this list).</b> What the IPAM monitors. Removing one stops
        address space in it being inventoried; it does not delete anything.
        <br />
        <b>3. A member&apos;s discovery Region.</b> A resource discovery is one AWS resource per
        account however many Regions it covers, so enrolling an account does not multiply per
        Region — and the discovered-resource reads are made from the home Region with the target
        Region as a parameter.
        <br />
        <b>4. A pool&apos;s locale.</b> Allocating and releasing CIDRs must be sent to the pool&apos;s
        own locale, not the home Region. A pool created with no locale cannot allocate at all, and
        Prism marks it as such.
      </Alert>

      <Typography sx={{ ...captionTextSx, mt: 1 }}>
        The home Region is always monitored and cannot be removed from this list. Adding Regions
        widens what is discovered — and, because the meter counts every active IP that is
        discovered, it widens what is billed.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );

  // The estimate, with its provenance stated as data rather than implied by a
  // number. There is no third rendering and there is deliberately no "$0.00":
  // an un-estimated bill is not a free one, and the two-stage flow exists
  // precisely so "Not estimated" is the exception rather than the default.
  const estimateBlock = () => (
    <Box>
      {estimateReady ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '1.05rem', fontWeight: 700, color: CK.navy }}>
              ≈ {activeIps.toLocaleString('en-US')} active IPs → ≈ {monthlyCost} / month
            </Typography>
            <Chip size="small" label="Prism estimate" sx={statusChipSx('info')} />
          </Stack>
          <Typography sx={{ ...captionTextSx, mt: 0.5 }}>
            Counted from attached ENIs in the accounts and Regions selected above — a lower bound.
            AWS&apos;s own meter may differ.
          </Typography>
        </>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '1.05rem', fontWeight: 700, color: CK.navy }}>
              —
            </Typography>
            <Chip
              size="small"
              label={shouldPollEstimate ? 'Estimating…' : 'Not estimated'}
              sx={statusChipSx(shouldPollEstimate ? 'info' : 'warning')}
            />
          </Stack>
          <Typography sx={{ ...captionTextSx, mt: 0.5 }}>
            {shouldPollEstimate
              ? 'Prism is granting read permissions in each account and counting the IP addresses '
                + 'attached to their network interfaces. Nothing has been created and nothing is '
                + 'billing yet.'
              : 'Prism could not estimate your active-IP count. The charge is real regardless — '
                + 'enable only if you accept an unknown monthly figure.'}
          </Typography>
          {!shouldPollEstimate && (
            <Button
              size="small"
              sx={{ ...quietButtonSx, mt: 1 }}
              onClick={() => { setEstimateGaveUp(false); reloadEstimate(); }}
            >
              Try the estimate again
            </Button>
          )}
        </>
      )}
      {/* The coverage line the service writes beside the value. An estimate that
          covered 3 of 18 accounts is a lower bound by a factor of six, and a
          partial recorded as though it were complete is the shape of a billing
          dispute. */}
      {draft?.note && (
        <Typography sx={{ ...captionTextSx, mt: 1 }}>{draft.note}</Typography>
      )}
    </Box>
  );

  const scopeStep = () => {
    const meteredAccount = costPreview?.meteredAccount || draft?.meteredAccount || '';
    return (
      <Box sx={{ py: 2 }}>
        <AdvancedTierAlert sx={{ mb: 2 }} />

        <Box sx={{ ...insetPanelSx, mb: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 0.5 }}>
            Metered scope — this is what you will pay for
          </Typography>
          <Typography sx={{ ...captionTextSx, mb: 2 }}>
            The set of accounts and the set of operating Regions are the only levers on this bill.
            There is no per-OU exclusion: every active IP discovered inside this scope is metered,
            whether or not it sits in an IPAM pool.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ py: 0.5 }}
          >
            <Typography sx={bodyTextSx}>
              <b>{selectedCount} account{selectedCount === 1 ? '' : 's'}</b>
              {selectedCount > 0 && selectedCount <= 3
                ? ` — ${memberAccountIds.map(accountLabel).join(', ')}`
                : ''}
            </Typography>
            <Button size="small" sx={quietButtonSx} onClick={() => setActive(ACCOUNTS_STEP)}>
              Edit accounts
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ py: 0.5, mb: 1.5 }}
          >
            <Typography sx={bodyTextSx}>
              <b>{operatingRegions.length} Region{operatingRegions.length === 1 ? '' : 's'}</b> —{' '}
              {operatingRegions.join(', ')}
            </Typography>
            <Button size="small" sx={quietButtonSx} onClick={() => setActive(REGIONS_STEP)}>
              Edit Regions
            </Button>
          </Stack>

          {estimateBlock()}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            {adopted
              ? 'The charge lands on an IPAM you already own'
              : `The charge lands on ${hostAccountLabel}`}
          </AlertTitle>
          {adopted ? (
            <>
              Prism {willAdopt ? 'will not create' : 'did not create'} this IPAM — it{' '}
              {willAdopt ? 'will adopt' : 'adopted'} the Advanced-tier IPAM{' '}
              {adoptedIpamId ? <b>{adoptedIpamId}</b> : 'that already exists'} in {hostAccountLabel},
              and it will never delete it. Its meter is already yours. What you are agreeing to
              here is <b>{selectedCount} more account{selectedCount === 1 ? '' : 's'}</b> being
              discovered onto that same meter, which raises the active-IP count it charges for.
            </>
          ) : (
            <>
              The IPAM bill for all {selectedCount} enrolled account
              {selectedCount === 1 ? '' : 's'} lands on {hostAccountLabel}.
            </>
          )}
          <br /><br />
          AWS defaults <code>MeteredAccount</code> to <code>ipam-owner</code>
          {meteredAccount ? <> (AWS currently reports <code>{meteredAccount}</code>)</> : null}, so the
          account that owns the IPAM is charged for every active IP it manages. Only the accounts you
          enrol here are managed — no other account is billed or visible. Moving the charge to each
          resource&apos;s own account requires AWS Organizations integration and an IPAM created by a
          delegated administrator — neither of which Prism uses. Prism does not change this setting
          and cannot change it for you.
        </Alert>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            Advanced → Free is not an undo
          </AlertTitle>
          AWS refuses the downgrade until all five of these are gone:
          <br />
          1. every pool in a private scope · 2. every non-default private scope · 3. every pool whose
          locale is not the home Region · 4. every resource-discovery association other than the
          host&apos;s own default one — which means every account you enrol here · 5. every
          allocation not owned by the IPAM owner, including ones a member account made for itself.
          <br /><br />
          {adopted ? (
            <>
              Until then the Advanced meter keeps running. {IPAM_ADOPTED_TEARDOWN_SUMMARY} Only
              deleting {adoptedIpamId || 'the IPAM'} yourself, in the host account, stops that
              meter.
            </>
          ) : (
            <>
              Until then the Advanced meter keeps running. Turning IPAM off entirely deletes the
              IPAM, and billing stops when AWS reports the IPAM deleted.
            </>
          )}
        </Alert>

        {/* IMMEDIATELY ABOVE THE CONSENT CONTROL, never in a footnote and never
            only on the host step four screens back. Adoption changes what the
            switch below means: the operator is not agreeing to a resource Prism
            creates and can delete, they are agreeing to put N discoveries onto a
            meter attached to a resource of their own that Prism will leave behind. */}
        {adopted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {willAdopt ? 'Prism will adopt' : 'Prism adopted'}{' '}
              {adoptedIpamId || 'the IPAM that already exists here'} — it{' '}
              {willAdopt ? 'will not create' : 'did not create'} one
            </AlertTitle>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0 }}>
              <Typography component="li" sx={{ ...bodyTextSx, color: 'inherit' }}>
                AWS allows one IPAM per Region, and {hostAccountLabel} already has one in{' '}
                {homeRegion}. Prism {willAdopt ? 'uses' : 'used'} it instead of creating its own.
              </Typography>
              <Typography component="li" sx={{ ...bodyTextSx, color: 'inherit' }}>
                <b>Prism will never delete it.</b> Turning VPC IPAM off removes Prism&apos;s own
                associations and leaves this IPAM, and its meter, running.
              </Typography>
              <Typography component="li" sx={{ ...bodyTextSx, color: 'inherit' }}>
                The {selectedCount} member discover{selectedCount === 1 ? 'y' : 'ies'} you enrol
                {willAdopt ? ' will be' : ' are'} associated <b>into your own IPAM</b>, so their
                active IPs are billed to the account that owns it — <b>your account</b>, on a
                resource Prism does not control the lifecycle of.
              </Typography>
            </Stack>
          </Alert>
        )}

        <Box sx={insetPanelSx}>
          <FormControlLabel
            control={
              <Switch
                checked={costAcknowledged}
                onChange={(e) => setCostAcknowledged(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: IPAM_COLOR },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: IPAM_COLOR },
                }}
              />
            }
            label={(
              <Typography sx={{ ...bodyTextSx, fontWeight: 600, color: CK.navy }}>
                I understand VPC IPAM Advanced tier is billed, that the meter covers every active IP
                in the accounts and Regions selected above whether or not they are in a pool, and
                that the charge lands on {hostAccountLabel}.
                {adopted && (
                  <>
                    {' '}I also understand that {adoptedIpamId || 'the existing IPAM'} is not
                    Prism&apos;s — Prism {willAdopt ? 'will adopt' : 'adopted'} it, will never
                    delete it, and the accounts enrolled here are metered on it.
                  </>
                )}
              </Typography>
            )}
            sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
          />
          <Typography sx={{ ...captionTextSx, mt: 1 }}>
            This is recorded with your name, the time, and the active-IP figure shown above — and
            the server refuses to start the enablement without it.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  };

  // What the Review step says Prism is about to do with the IPAM itself. FOUR
  // ANSWERS, because "creates a new one" is only one of them and asserting it in
  // the other three is a promise about who owns and who pays.
  const ipamReviewValue = () => {
    if (adopted) {
      return `${willAdopt ? 'Adopting' : 'Adopted'} ${adoptedIpamId || 'the existing IPAM'} — `
        + 'Prism will not create one and will never delete it';
    }
    if (hostDisposition === HOST_IPAM_OURS) {
      return `Continuing onto Prism's existing IPAM${hostIpamId ? ` ${hostIpamId}` : ''}`;
    }
    if (hostUnknown) {
      return 'Prism could not check this account for an existing IPAM — it re-checks before enabling';
    }
    return 'Prism creates and owns a new IPAM here';
  };

  const reviewStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
      <ReviewRow label="Host account" value={hostAccountLabel} />
      <ReviewRow label="Home Region" value={homeRegion} />
      {/* The adopt/create decision is a REVIEW LINE, not an implication of the
          host account. It is the difference between a resource Prism owns and
          deletes and one the customer owns and keeps paying for — and the
          unchecked case is stated as unchecked rather than as "creates one". */}
      <ReviewRow label="IPAM" value={ipamReviewValue()} />
      <ReviewRow
        label="Enrolled accounts"
        value={selectedCount <= 3
          ? memberAccountIds.map(accountLabel).join(', ')
          : `${selectedCount} accounts`}
      />
      <ReviewRow label="Operating Regions" value={operatingRegions.join(', ')} />
      <ReviewRow
        label="Estimated monthly cost"
        value={estimateReady
          ? `≈ ${monthlyCost} — ≈ ${activeIps.toLocaleString('en-US')} active IPs`
          : 'Not estimated — the charge applies regardless'}
      />

      {/* BOTH cost alerts are restated here. This is the last screen before the
          click, and the price cannot be something the operator read two steps ago. */}
      <AdvancedTierAlert sx={{ mt: 2 }} />

      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
          The charge lands on {hostAccountLabel}, and the meter starts with the IPAM
        </AlertTitle>
        Every active IP in the {selectedCount} account{selectedCount === 1 ? '' : 's'} and{' '}
        {operatingRegions.length} Region{operatingRegions.length === 1 ? '' : 's'} above is metered,
        whether or not it sits in an IPAM pool.{' '}
        {adopted
          ? IPAM_ADOPTED_TEARDOWN_SUMMARY
          : 'Turning IPAM off later deletes the IPAM and destroys all CIDR address history, '
            + 'irreversibly; billing stops when AWS reports the IPAM deleted.'}
      </Alert>

      {!costAcknowledged && (
        <Alert severity="info" sx={{ mt: 2 }}>
          The Advanced-tier cost has not been acknowledged. Go back to <b>Scope &amp; cost</b> and
          turn on the acknowledgement — the server refuses to enable without it.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );

  const body = () => {
    if (active === HOST_STEP) return hostStep();
    if (active === ACCOUNTS_STEP) return accountsStep();
    if (active === REGIONS_STEP) return regionsStep();
    if (active === SCOPE_STEP) return scopeStep();
    return reviewStep();
  };

  const nextLabel = () => {
    if (active === REGIONS_STEP) return beginning ? 'Preparing your estimate…' : 'Continue to cost';
    return 'Next';
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LanOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Set up VPC IPAM"
          subtitle="Inventory the address space of the accounts you enrol"
          color={IPAM_COLOR}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          {priorFailure && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              The previous setup attempt failed: {priorFailure}. Submitting again clears that state
              and retries; the configuration row, its teardown ledger and any IPAM handle it recorded
              are kept rather than replaced.
            </Alert>
          )}
          <Stepper
            activeStep={active}
            alternativeLabel
            sx={{
              mb: 3,
              overflowX: 'auto',
              pb: 1,
              '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
              '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
              '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
              '& .MuiStepIcon-root.Mui-active': { color: IPAM_COLOR },
              '& .MuiStepIcon-root.Mui-completed': { color: IPAM_COLOR },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {body()}
          <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
            <Button disabled={active === HOST_STEP || beginning || submitting} onClick={back} sx={quietButtonSx}>
              Back
            </Button>
            {active < REVIEW_STEP ? (
              <Button
                variant="contained"
                disabled={!stepComplete(active) || beginning}
                onClick={next}
                sx={ipamButtonSx}
              >
                {nextLabel()}
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={submitting || !canSubmit}
                onClick={submit}
                sx={ipamButtonSx}
              >
                {/* The last string before the click contains the word "billed". */}
                {submitting ? 'Enabling…' : 'Enable IPAM (Advanced tier — billed)'}
              </Button>
            )}
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
}
